import * as React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { centeredPosition, fitToViewport } from "./centerPosition";
import { andockGeometrie, type AndockSeite } from "./andocken";

/**
 * Darstellungsmodi einer Fenster-Instanz.
 * - `normal`    schwebendes Fenster mit Titelleiste
 * - `maximized` volle Content-Fläche, Titelleiste bleibt
 * - `minimized` als Chip im Dock (bleibt gemountet, nur display:none)
 * - `page`      VOLLSEITE: volle Content-Fläche OHNE Titelleiste; bedient wird
 *               sie über die zwei Knöpfe der Seite selbst („Als Fenster",
 *               „Minimieren"). Bewusst ein dritter Modus DERSELBEN Instanz —
 *               der Wechsel Seite ↔ Fenster ändert nur state/position/size,
 *               es gibt keinen Unmount und damit keinen Datenverlust.
 */
export type WindowState = "normal" | "minimized" | "maximized" | "page";

export type ManagedWindow = {
  /** Stabile ID pro logischer Instanz, z.B. "risk-edit-123". */
  id: string;
  title: string;
  icon?: React.ReactNode;
  /**
   * Render-Prop statt vorgefertigtes Element. Vermeidet stale closures wenn
   * der Trigger-Component schon unmounted ist, das Window aber noch lebt.
   */
  render: () => React.ReactNode;
  /**
   * Optionaler Inhalt links in der Titelleiste — vor Icon und Titel. Wird
   * z. B. vom Kennzahlen-Wrapper für einen "← Zurück"-Button genutzt.
   */
  headerLeftSlot?: React.ReactNode;
  /**
   * Titelleiste im helleren Aubergine der PDF-Leiste statt im dunklen
   * Marken-Violett (User 04.10.2026: „die Windows-Leiste oben so hell wie beim
   * Bearbeiten“). Für Dokument-Fenster, deren Inhalt direkt darunter die
   * Kopfzeile des PDF-Viewers/-Editors zeigt — beide Zeilen haben dann einen Ton.
   */
  titelleisteHell?: boolean;
  /**
   * Handy: ohne KMVS-Titelleiste (X / –) — der Inhalt bringt seinen eigenen
   * Kopf mit (Mail schreiben wie Outlook iOS). Tablet/Desktop unverändert.
   */
  ohneTitelleisteAufHandy?: boolean;
  state: WindowState;
  position: { x: number; y: number };
  size: { width: number; height: number };
  /** Pre-Maximize Snapshot — wird beim Restore wiederhergestellt. */
  preMax?: { position: { x: number; y: number }; size: { width: number; height: number } };
  zIndex: number;
  /** Per-Window Dirty-Flag — gesetzt von eingebetteten Formularen via onDirtyChange. */
  dirty?: boolean;
  /**
   * Nur gesetzt, wenn aus dem Vollseiten-Modus heraus minimiert wurde. Damit
   * kommt die Vollseite über den Dock-Chip wieder ALS Vollseite zurück und
   * nicht als schwebendes Fenster. Für maximierte Fenster bewusst NICHT
   * gesetzt — deren Verhalten (zurück als normales Fenster) bleibt unverändert.
   */
  preMinimizeState?: "page";
  /**
   * Am linken bzw. rechten Rand angedockt: halbe Fensterfläche, volle Höhe
   * (User 26.10.2026). Bleibt beim Maximieren/Wiederherstellen und beim
   * Minimieren erhalten; wird die Fläche breiter oder schmaler, zieht das
   * Fenster mit (FloatingWindow). Verschieben oder Größe ziehen löst es.
   */
  angedockt?: AndockSeite;
  /** Größe vor dem Andocken — die bekommt das Fenster zurück, wenn man es vom Rand wegzieht. */
  vorAndocken?: { width: number; height: number };
  /**
   * Läuft in einem eigenen Browserfenster (User 26.10.2026: „um das einem
   * anderen Bildschirm zuzuordnen“). Im Hauptfenster ist es dann unsichtbar
   * und steht auch nicht im Dock (User 27.10.2026: „dann ist das Fenster
   * gelöst“); der Inhalt bleibt gemountet (FloatingWindow).
   */
  browserFenster?: boolean;
  /**
   * Das Browserfenster, aus dem heraus dieses Fenster geöffnet wurde — sein
   * eigenes Browserfenster geht dann dort auf statt an der gemerkten Stelle
   * (User 01.11.2026: Vorschau aus einem ausgelagerten Fenster).
   */
  browserFensterNeben?: Window;
};

export type OpenWindowConfig = {
  id: string;
  title: string;
  icon?: React.ReactNode;
  render: () => React.ReactNode;
  headerLeftSlot?: React.ReactNode;
  /** Siehe {@link ManagedWindow.titelleisteHell}. */
  titelleisteHell?: boolean;
  /** Siehe {@link ManagedWindow.ohneTitelleisteAufHandy}. */
  ohneTitelleisteAufHandy?: boolean;
  /** Default-Position beim ersten Öffnen. */
  initialPosition?: { x: number; y: number };
  /** Default-Größe beim ersten Öffnen. */
  initialSize?: { width: number; height: number };
  /**
   * Fenster zentriert im Viewport öffnen statt mit Kaskaden-Offset
   * (Mail→Vorgang SP3). Eine explizite initialPosition gewinnt.
   */
  center?: boolean;
  /** Fenster gleich maximiert (fullpage) öffnen. Restore nutzt initialPosition/-Size. */
  initialMaximized?: boolean;
  /**
   * Darstellung beim ersten Öffnen. Gewinnt gegen `initialMaximized`, das als
   * Alias für `"maximized"` erhalten bleibt (Kontakt-, Vertrags-, Dokumenten-
   * und Mail-Fenster bleiben damit unverändert). `"page"` öffnet als Vollseite;
   * „Als Fenster" fällt dann auf `initialPosition`/`initialSize` zurück.
   */
  initialMode?: "page" | "maximized" | "normal";
  /**
   * Gleich als eigenes Browserfenster öffnen, neben diesem Browserfenster —
   * für Fenster, die aus einem ausgelagerten Fenster heraus aufgehen (z. B.
   * die Vorschau eines Anhangs). Sonst erschienen sie im Hauptfenster, oft auf
   * dem anderen Bildschirm. Blockiert der Browser das neue Fenster, bleibt es
   * im Hauptfenster (Hinweis in `useBrowserFenster`).
   */
  browserFensterNeben?: Window;
};

type Action =
  | {
      type: "open";
      config: OpenWindowConfig;
      /**
       * Gemessene Größe des Fenster-Layers (T-27). Der Layer liegt unter der
       * App-Kopfzeile und ist deutlich niedriger als der Browser-Viewport;
       * gegen ihn wird die Größe geklemmt und zentriert. Fehlt er (Tests, noch
       * nicht gemessen), gilt das bisherige Verhalten.
       */
      viewport?: { width: number; height: number };
    }
  | { type: "close"; id: string }
  | { type: "minimize"; id: string }
  | { type: "maximize"; id: string }
  | { type: "restore"; id: string }
  | { type: "focus"; id: string }
  | { type: "move"; id: string; position: { x: number; y: number } }
  | { type: "resize"; id: string; size: { width: number; height: number }; position: { x: number; y: number } }
  | { type: "dock"; id: string; seite: AndockSeite; flaeche: { width: number; height: number } }
  | { type: "setTitle"; id: string; title: string }
  | { type: "setDirty"; id: string; dirty: boolean }
  | { type: "setMode"; id: string; mode: "page" | "normal" }
  | { type: "setBrowserFenster"; id: string; an: boolean }
  | { type: "dismissTopMaximized" };

type State = {
  windows: ManagedWindow[];
  nextZ: number;
};

const DEFAULT_SIZE = { width: 900, height: 640 };

const offsetForNew = (existing: ManagedWindow[]): { x: number; y: number } => {
  // Leichter Kaskaden-Offset, damit neu geöffnete Fenster nicht exakt aufeinander liegen.
  const baseX = 80;
  const baseY = 80;
  const step = 28;
  const i = existing.length;
  return { x: baseX + i * step, y: baseY + i * step };
};

/** Ein Fenster aus der Vollseite zurück in ein schwebendes Fenster überführen. */
const toFloating = (w: ManagedWindow): ManagedWindow => ({
  ...w,
  state: "normal",
  position: w.preMax?.position ?? w.position,
  size: w.preMax?.size ?? w.size,
  preMax: undefined,
  preMinimizeState: undefined,
});

/**
 * Höchstens EINE Vollseite gleichzeitig: alle anderen Vollseiten fallen auf ein
 * normales Fenster zurück. Sonst verdecken sich zwei Vollseiten vollständig und
 * die untere wäre — mangels Titelleiste und Dock-Eintrag — unerreichbar.
 */
const demoteOtherPages = (windows: ManagedWindow[], keepId: string): ManagedWindow[] =>
  windows.map((w) => (w.id !== keepId && w.state === "page" ? toFloating(w) : w));

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "open": {
      const existingIdx = state.windows.findIndex((w) => w.id === action.config.id);
      if (existingIdx >= 0) {
        // Schon offen → nur fokussieren und ggf. aus minimiert holen. Eine
        // minimierte VOLLSEITE kehrt als Vollseite zurück — sonst käme dasselbe
        // Fenster je nach Weg (Dock-Chip vs. erneuter open-Aufruf) anders zurück.
        const w = state.windows[existingIdx];
        const restoredState: WindowState =
          w.state === "minimized" ? (w.preMinimizeState ?? "normal") : w.state;
        const updated: ManagedWindow = {
          ...w,
          state: restoredState,
          preMinimizeState: undefined,
          zIndex: state.nextZ,
        };
        let windows = [...state.windows];
        windows[existingIdx] = updated;
        if (restoredState === "page") windows = demoteOtherPages(windows, updated.id);
        return { windows, nextZ: state.nextZ + 1 };
      }
      const wunschSize = action.config.initialSize ?? DEFAULT_SIZE;
      // T-27: Ist der Layer bekannt, passt das Fenster hinein (Fußzeile bleibt
      // auf dem Laptop erreichbar). Große Monitore behalten die Wunschgröße.
      const normalSize = action.viewport ? fitToViewport(wunschSize, action.viewport) : wunschSize;
      const zentrierFlaeche =
        action.viewport ??
        (typeof window !== "undefined"
          ? { width: window.innerWidth, height: window.innerHeight }
          : null);
      const normalPosition =
        action.config.initialPosition ??
        (action.config.center && zentrierFlaeche
          ? centeredPosition(normalSize, zentrierFlaeche)
          : offsetForNew(state.windows));
      // `initialMode` gewinnt; `initialMaximized` bleibt der Alias für "maximized".
      const mode: "page" | "maximized" | "normal" =
        action.config.initialMode ??
        (action.config.initialMaximized === true ? "maximized" : "normal");
      const w: ManagedWindow = {
        id: action.config.id,
        title: action.config.title,
        icon: action.config.icon,
        render: action.config.render,
        headerLeftSlot: action.config.headerLeftSlot,
        titelleisteHell: action.config.titelleisteHell,
        ohneTitelleisteAufHandy: action.config.ohneTitelleisteAufHandy,
        state: mode,
        position: normalPosition,
        size: normalSize,
        // „Wiederherstellen" bzw. „Als Fenster" fällt auf diese Normal-Maße zurück.
        preMax: mode === "normal" ? undefined : { position: normalPosition, size: normalSize },
        zIndex: state.nextZ,
        ...(action.config.browserFensterNeben
          ? { browserFenster: true, browserFensterNeben: action.config.browserFensterNeben }
          : {}),
      };
      const opened = [...state.windows, w];
      return {
        windows: mode === "page" ? demoteOtherPages(opened, w.id) : opened,
        nextZ: state.nextZ + 1,
      };
    }
    case "close":
      return { ...state, windows: state.windows.filter((w) => w.id !== action.id) };
    case "minimize":
      return {
        ...state,
        windows: state.windows.map((w) =>
          w.id === action.id
            ? {
                ...w,
                state: "minimized" as const,
                // Nur die Vollseite merkt sich ihren Modus (siehe preMinimizeState).
                preMinimizeState: w.state === "page" ? ("page" as const) : w.preMinimizeState,
              }
            : w,
        ),
      };
    case "maximize":
      return {
        ...state,
        nextZ: state.nextZ + 1,
        windows: state.windows.map((w) =>
          w.id === action.id
            ? {
                ...w,
                state: "maximized" as const,
                preMax: w.preMax ?? { position: w.position, size: w.size },
                zIndex: state.nextZ,
              }
            : w,
        ),
      };
    case "restore": {
      const idx = state.windows.findIndex((w) => w.id === action.id);
      if (idx < 0) return state;
      const w = state.windows[idx];
      // Eine minimierte Vollseite kommt als Vollseite zurück; `preMax` bleibt
      // erhalten, damit „Als Fenster" weiterhin auf die Ausgangsmaße fällt.
      const updated: ManagedWindow =
        w.preMinimizeState === "page"
          ? { ...w, state: "page", preMinimizeState: undefined, zIndex: state.nextZ }
          : {
              ...w,
              state: "normal",
              position: w.preMax?.position ?? w.position,
              size: w.preMax?.size ?? w.size,
              preMax: undefined,
              zIndex: state.nextZ,
            };
      let windows = [...state.windows];
      windows[idx] = updated;
      if (updated.state === "page") windows = demoteOtherPages(windows, updated.id);
      return { ...state, nextZ: state.nextZ + 1, windows };
    }
    case "focus":
      return {
        ...state,
        nextZ: state.nextZ + 1,
        windows: state.windows.map((w) =>
          w.id === action.id ? { ...w, zIndex: state.nextZ } : w,
        ),
      };
    // Verschieben und Größe ziehen lösen ein angedocktes Fenster vom Rand.
    case "move":
      return {
        ...state,
        windows: state.windows.map((w) =>
          w.id === action.id
            ? { ...w, position: action.position, angedockt: undefined, vorAndocken: undefined }
            : w,
        ),
      };
    case "resize":
      return {
        ...state,
        windows: state.windows.map((w) =>
          w.id === action.id
            ? {
                ...w,
                position: action.position,
                size: action.size,
                angedockt: undefined,
                vorAndocken: undefined,
              }
            : w,
        ),
      };
    case "dock": {
      const idx = state.windows.findIndex((w) => w.id === action.id);
      if (idx < 0) return state;
      const w = state.windows[idx];
      const { position, size } = andockGeometrie(action.seite, action.flaeche);
      const gleich =
        w.state === "normal" &&
        w.angedockt === action.seite &&
        w.position.x === position.x &&
        w.position.y === position.y &&
        w.size.width === size.width &&
        w.size.height === size.height;
      if (gleich) return state;
      const windows = [...state.windows];
      windows[idx] = {
        ...w,
        state: "normal",
        position,
        size,
        preMax: undefined,
        angedockt: action.seite,
        // Schon angedockt (anderer Rand, Fläche geändert): die ursprüngliche
        // Größe bleibt die vor dem ERSTEN Andocken.
        vorAndocken: w.vorAndocken ?? w.size,
      };
      return { ...state, windows };
    }
    // setTitle/setDirty geben bei UNVERÄNDERTEM Wert exakt denselben State
    // zurück. `map` würde sonst bei jedem Aufruf ein neues Array liefern, der
    // useMemo auf state.windows recomputen, die Callbacks der eingebetteten
    // Formulare neu erzeugen und deren useEffect([answers, onAnswersChange])
    // erneut feuern → Render-Rückkopplung.
    case "setTitle": {
      const idx = state.windows.findIndex((w) => w.id === action.id);
      if (idx < 0 || state.windows[idx].title === action.title) return state;
      const windows = [...state.windows];
      windows[idx] = { ...windows[idx], title: action.title };
      return { ...state, windows };
    }
    case "setDirty": {
      const idx = state.windows.findIndex((w) => w.id === action.id);
      if (idx < 0 || state.windows[idx].dirty === action.dirty) return state;
      const windows = [...state.windows];
      windows[idx] = { ...windows[idx], dirty: action.dirty };
      return { ...state, windows };
    }
    case "setMode": {
      const idx = state.windows.findIndex((w) => w.id === action.id);
      if (idx < 0) return state;
      const w = state.windows[idx];
      if (w.state === action.mode) return state;
      const updated: ManagedWindow =
        action.mode === "page"
          ? {
              ...w,
              state: "page",
              // Wie maximize: die Ausgangsmaße merken, damit „Als Fenster"
              // wieder dorthin zurückfällt.
              preMax: w.preMax ?? { position: w.position, size: w.size },
              preMinimizeState: undefined,
              zIndex: state.nextZ,
            }
          : { ...toFloating(w), zIndex: state.nextZ };
      let windows = [...state.windows];
      windows[idx] = updated;
      if (action.mode === "page") windows = demoteOtherPages(windows, action.id);
      return { ...state, nextZ: state.nextZ + 1, windows };
    }
    case "setBrowserFenster": {
      const idx = state.windows.findIndex((w) => w.id === action.id);
      if (idx < 0 || !!state.windows[idx].browserFenster === action.an) return state;
      const windows = [...state.windows];
      // Zurückgeholt kommt es nach vorn — wie aus dem Dock wiederhergestellt.
      windows[idx] = action.an
        ? { ...windows[idx], browserFenster: true }
        : { ...windows[idx], browserFenster: undefined, browserFensterNeben: undefined, zIndex: state.nextZ };
      return { windows, nextZ: action.an ? state.nextZ : state.nextZ + 1 };
    }
    case "dismissTopMaximized": {
      // Ein ausgelagertes Fenster ist im Hauptfenster nicht zu sehen.
      const maxWins = state.windows.filter((w) => w.state === "maximized" && !w.browserFenster);
      if (maxWins.length === 0) return state;
      const top = maxWins.reduce((a, b) => (b.zIndex > a.zIndex ? b : a));
      return {
        ...state,
        // zIndex/nextZ bewusst UNVERÄNDERT: das restaurierte Fenster darf nicht
        // über ein evtl. noch maximiertes Fenster nach vorn springen.
        windows: state.windows.map((w) =>
          w.id === top.id
            ? {
                ...w,
                state: "normal" as const,
                position: w.preMax?.position ?? w.position,
                size: w.preMax?.size ?? w.size,
                preMax: undefined,
              }
            : w,
        ),
      };
    }
    default:
      return state;
  }
};

const initialState: State = { windows: [], nextZ: 100 };

type WindowManagerCtx = {
  windows: ManagedWindow[];
  openWindow: (config: OpenWindowConfig) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  maximizeWindow: (id: string) => void;
  restoreWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  moveWindow: (id: string, position: { x: number; y: number }) => void;
  resizeWindow: (
    id: string,
    size: { width: number; height: number },
    position: { x: number; y: number },
  ) => void;
  /** Dockt ein Fenster an den linken/rechten Rand der Fläche `flaeche` an (halbe Breite, volle Höhe). */
  dockWindow: (id: string, seite: AndockSeite, flaeche: { width: number; height: number }) => void;
  /** Aktualisiert den Titel eines offenen Fensters — z. B. live während der User Felder ausfüllt. */
  setWindowTitle: (id: string, title: string) => void;
  /** Setzt das Dirty-Flag eines Fensters — steuert den Close-Confirm-Guard in FloatingWindow. */
  setWindowDirty: (id: string, dirty: boolean) => void;
  /**
   * Eigene Schließen-Rückfrage eines Fensterinhalts (Kontaktakte, 28.09.2026):
   * Statt des allgemeinen Verwerfen/Minimieren-Dialogs fragt der Inhalt selbst
   * („Speichern | Verwerfen | Abbrechen"). Die Funktion liefert `true`, wenn
   * geschlossen werden darf. `null` entfernt sie wieder.
   */
  setWindowCloseGuard: (id: string, guard: (() => Promise<boolean>) | null) => void;
  /**
   * Schließen MIT Rückfrage: fragt den registrierten Guard (falls vorhanden)
   * und schließt nur bei `true`. Liefert `true`, wenn geschlossen wurde.
   */
  requestCloseWindow: (id: string) => Promise<boolean>;
  /** Hat das Fenster eine eigene Schließen-Rückfrage? */
  hasWindowCloseGuard: (id: string) => boolean;
  /**
   * Wechselt zwischen Vollseite und schwebendem Fenster. Reine State-Änderung
   * an DERSELBEN Instanz — kein Unmount, deshalb bleiben alle Eingaben erhalten.
   */
  setWindowMode: (id: string, mode: "page" | "normal") => void;
  /**
   * Fenster in ein eigenes Browserfenster auslagern (`true`) bzw. zurück ins
   * Hauptfenster holen (`false`). Das Browserfenster selbst öffnet FloatingWindow.
   */
  setWindowBrowserFenster: (id: string, an: boolean) => void;
  /** True, wenn mindestens ein Fenster maximiert (fullpage) ist — für den Topbar-Zurück-Intercept (B1). */
  hasMaximizedWindow: boolean;
  /** Restauriert das oberste maximierte Fenster zu floating (Topbar-Zurück statt navigate(-1)). */
  dismissTopMaximized: () => void;
  /**
   * Der WindowLayer meldet hier sein Element an (T-27), damit `openWindow`
   * die tatsächlich verfügbare Fläche messen kann.
   */
  registerLayer: (el: HTMLElement | null) => void;
};

const WindowManagerContext = createContext<WindowManagerCtx | null>(null);

export const WindowManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const layerRef = React.useRef<HTMLElement | null>(null);
  const registerLayer = useCallback((el: HTMLElement | null) => {
    layerRef.current = el;
  }, []);
  const openWindow = useCallback((config: OpenWindowConfig) => {
    const el = layerRef.current;
    const viewport =
      el && el.clientWidth > 0 && el.clientHeight > 0
        ? { width: el.clientWidth, height: el.clientHeight }
        : undefined;
    dispatch({ type: "open", config, viewport });
  }, []);
  const closeWindow = useCallback((id: string) => dispatch({ type: "close", id }), []);
  const minimizeWindow = useCallback((id: string) => dispatch({ type: "minimize", id }), []);
  const maximizeWindow = useCallback((id: string) => dispatch({ type: "maximize", id }), []);
  const restoreWindow = useCallback((id: string) => dispatch({ type: "restore", id }), []);
  const focusWindow = useCallback((id: string) => dispatch({ type: "focus", id }), []);
  const moveWindow = useCallback(
    (id: string, position: { x: number; y: number }) => dispatch({ type: "move", id, position }),
    [],
  );
  const resizeWindow = useCallback(
    (id: string, size: { width: number; height: number }, position: { x: number; y: number }) =>
      dispatch({ type: "resize", id, size, position }),
    [],
  );
  const dockWindow = useCallback(
    (id: string, seite: AndockSeite, flaeche: { width: number; height: number }) =>
      dispatch({ type: "dock", id, seite, flaeche }),
    [],
  );
  const setWindowTitle = useCallback(
    (id: string, title: string) => dispatch({ type: "setTitle", id, title }),
    [],
  );
  const setWindowDirty = useCallback(
    (id: string, dirty: boolean) => dispatch({ type: "setDirty", id, dirty }),
    [],
  );
  const closeGuardsRef = React.useRef(new Map<string, () => Promise<boolean>>());
  const setWindowCloseGuard = useCallback((id: string, guard: (() => Promise<boolean>) | null) => {
    if (guard) closeGuardsRef.current.set(id, guard);
    else closeGuardsRef.current.delete(id);
  }, []);
  const hasWindowCloseGuard = useCallback((id: string) => closeGuardsRef.current.has(id), []);
  const requestCloseWindow = useCallback(async (id: string) => {
    const guard = closeGuardsRef.current.get(id);
    if (guard && !(await guard())) return false;
    closeGuardsRef.current.delete(id);
    dispatch({ type: "close", id });
    return true;
  }, []);
  const setWindowMode = useCallback(
    (id: string, mode: "page" | "normal") => dispatch({ type: "setMode", id, mode }),
    [],
  );
  const setWindowBrowserFenster = useCallback(
    (id: string, an: boolean) => dispatch({ type: "setBrowserFenster", id, an }),
    [],
  );
  const dismissTopMaximized = useCallback(
    () => dispatch({ type: "dismissTopMaximized" }),
    [],
  );

  const value = useMemo<WindowManagerCtx>(
    () => ({
      windows: state.windows,
      openWindow,
      closeWindow,
      minimizeWindow,
      maximizeWindow,
      restoreWindow,
      focusWindow,
      moveWindow,
      resizeWindow,
      dockWindow,
      setWindowTitle,
      setWindowDirty,
      setWindowCloseGuard,
      requestCloseWindow,
      hasWindowCloseGuard,
      setWindowMode,
      setWindowBrowserFenster,
      // Bewusst NICHT um "page" erweitert: der Topbar-Zurück-Intercept soll im
      // Vollseiten-Modus nicht greifen — dort führt „Zurück" durch die Seite.
      hasMaximizedWindow: state.windows.some((w) => w.state === "maximized" && !w.browserFenster),
      dismissTopMaximized,
      registerLayer,
    }),
    [
      state.windows,
      openWindow,
      closeWindow,
      minimizeWindow,
      maximizeWindow,
      restoreWindow,
      focusWindow,
      moveWindow,
      resizeWindow,
      dockWindow,
      setWindowTitle,
      setWindowDirty,
      setWindowCloseGuard,
      requestCloseWindow,
      hasWindowCloseGuard,
      setWindowMode,
      setWindowBrowserFenster,
      dismissTopMaximized,
      registerLayer,
    ],
  );

  return <WindowManagerContext.Provider value={value}>{children}</WindowManagerContext.Provider>;
};

/**
 * Wie {@link useWindowManager}, wirft aber nicht ohne Provider — für Bausteine,
 * die auch außerhalb der App-Shell gemountet werden (z. B. der Zuordnen-Dialog
 * der Telefonie) und dann auf einen Weg ohne Fenster ausweichen.
 */
export const useWindowManagerOptional = (): WindowManagerCtx | null =>
  useContext(WindowManagerContext) ?? null;

export const useWindowManager = (): WindowManagerCtx => {
  const ctx = useContext(WindowManagerContext);
  if (!ctx) throw new Error("useWindowManager must be used inside <WindowManagerProvider>");
  return ctx;
};
